import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router";
import { Logo } from "./Logo";
import { LogOut, Menu, X, Clock, RefreshCw, AlertTriangle } from "lucide-react";
import { getPortalSession, updatePortalExpiry, clearPortalSession } from "./PortalGuard";

const navItems = [
  { label: "Overview", path: "/overview" },
  { label: "Infrastructure", path: "/infrastructure" },
  { label: "Platforms", path: "/platforms" },
  { label: "Licensed Solutions", path: "/licensed-solutions" },
  { label: "Operating Model", path: "/operating-model" },
  { label: "Partners", path: "/partner-ecosystem" },
  { label: "Commercial", path: "/commercial-model" },
  { label: "Security", path: "/security-compliance" },
  { label: "Contact", path: "/management-contact" },
];

// ─── Session Timer Bar ────────────────────────────────────────────────────────

function SessionTimer() {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState<number>(0);
  const [renewing, setRenewing] = useState(false);
  const [renewError, setRenewError] = useState<string | null>(null);

  const recalc = useCallback(() => {
    const session = getPortalSession();
    if (!session) { setSecondsLeft(0); return; }
    const diff = Math.max(0, Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 1000));
    // Admin-pinned sessions (> 24h remaining) — suppress the timer entirely
    if (diff > 86400) { setSecondsLeft(-1); return; }
    setSecondsLeft(diff);
    if (diff === 0) {
      clearPortalSession();
      navigate("/", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    recalc();
    const id = setInterval(recalc, 1000);
    return () => clearInterval(id);
  }, [recalc]);

  const handleRenew = async () => {
    const session = getPortalSession();
    if (!session) return;
    setRenewing(true);
    setRenewError(null);
    try {
      const res = await fetch(`/api/access/${session.token}/renew`, { method: "POST" });
      const data = await res.json();
      if (data.success && data.expiresAt) {
        updatePortalExpiry(data.expiresAt);
        recalc();
      } else {
        setRenewError(data.message || "Renewal failed.");
      }
    } catch {
      setRenewError("Unable to connect. Try again.");
    } finally {
      setRenewing(false);
    }
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  const warning = secondsLeft > 0 && secondsLeft <= 180; // < 3 min
  const expired = secondsLeft === 0;

  if (secondsLeft === -1) return null; // admin-pinned session — no timer shown
  if (expired) return null;

  return (
    <div className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-2 text-xs border-b transition-colors ${
      warning
        ? "bg-amber-500/10 border-amber-500/20"
        : "bg-white/3 border-[#E5E7EB]/40"
    }`}>
      <div className="flex items-center gap-2">
        {warning
          ? <AlertTriangle className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
          : <Clock className="w-3.5 h-3.5 text-[#6B7280] flex-shrink-0" />
        }
        <span className={warning ? "text-amber-400 font-medium" : "text-[#6B7280]"}>
          Session expires in{" "}
          <span className={`font-mono font-semibold ${warning ? "text-amber-300" : "text-[#1F2933]"}`}>
            {pad(mins)}:{pad(secs)}
          </span>
        </span>
        {renewError && <span className="text-red-400 ml-2">{renewError}</span>}
      </div>
      <button
        onClick={handleRenew}
        disabled={renewing}
        className={`flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 ${
          warning
            ? "bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/30"
            : "bg-[#F3F4F6] hover:bg-[#E5E7EB] text-[#374151] border border-[#E5E7EB]"
        }`}
      >
        <RefreshCw className={`w-3 h-3 ${renewing ? "animate-spin" : ""}`} />
        {renewing ? "Renewing…" : "Renew +15 min"}
      </button>
    </div>
  );
}

// ─── Portal Layout ────────────────────────────────────────────────────────────

interface PortalLayoutProps {
  children: React.ReactNode;
}

export function PortalLayout({ children }: PortalLayoutProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleNavClick = (path: string) => {
    navigate(path);
    setMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#F8F6F4] flex flex-col">

      {/* Sticky nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="px-4 sm:px-6 h-14 flex items-center gap-3">

          {/* Logo */}
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2.5 flex-shrink-0"
          >
            <Logo className="w-12" />
            <span className="hidden sm:block text-sm font-semibold text-[#1F2933]">Leo Group</span>
          </button>

          {/* Desktop divider */}
          <div className="hidden md:block w-px h-5 bg-[#E5E7EB] flex-shrink-0" />

          {/* Desktop nav links */}
          <div className="hidden md:flex flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-0.5 min-w-max">
              {navItems.map((item) => {
                const isActive = pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`relative px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                      isActive
                        ? "text-[#7B1A1A]"
                        : "text-[#6B7280] hover:text-[#1F2933] hover:bg-[#F8F6F4]"
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-[#7B1A1A] rounded-full" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mobile spacer */}
          <div className="flex-1 md:hidden" />

          {/* Desktop Exit Portal */}
          <button
            onClick={() => navigate("/")}
            className="hidden md:flex flex-shrink-0 items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F2933] border border-[#E5E7EB] hover:border-[#D1D5DB] px-3 py-1.5 rounded transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Exit Portal
          </button>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden flex-shrink-0 p-2 -mr-1 text-[#4B5563] hover:text-[#1F2933] transition-colors"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>

        </div>

        {/* Session timer — sits below nav bar */}
        <SessionTimer />

        {/* Mobile dropdown menu */}
        {menuOpen && (
          <div className="md:hidden border-t border-[#E5E7EB] bg-white shadow-lg">
            <div className="px-3 py-2 space-y-0.5">
              {navItems.map((item) => {
                const isActive = pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavClick(item.path)}
                    className={`w-full text-left px-4 py-3 text-sm rounded-lg transition-colors flex items-center justify-between ${
                      isActive
                        ? "text-[#7B1A1A] bg-[#FDEAE0]/60 font-medium"
                        : "text-[#4B5563] hover:text-[#1F2933] hover:bg-[#F8F6F4]"
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7B1A1A] flex-shrink-0" />
                    )}
                  </button>
                );
              })}
            </div>
            <div className="px-3 pb-3 pt-1 border-t border-[#E5E7EB] mx-3">
              <button
                onClick={() => { navigate("/"); setMenuOpen(false); }}
                className="w-full text-left px-4 py-3 text-sm text-[#4B5563] hover:text-[#1F2933] hover:bg-[#F8F6F4] rounded-lg transition-colors flex items-center gap-2.5"
              >
                <LogOut className="w-4 h-4" />
                Exit Portal
              </button>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-8 py-10 sm:py-16">
        {children}
      </main>

      <footer className="bg-[#6b1515] mt-8 sm:mt-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-12 sm:py-16">

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8 mb-10 sm:mb-12 text-center sm:text-left">

            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4 justify-center sm:justify-start">
                <Logo className="w-14" white />
                <span className="text-sm font-semibold text-white">Leo Group</span>
              </div>
              <p className="text-white/50 text-xs leading-relaxed">
                Technology and infrastructure holding company operating across
                Africa and emerging markets.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Quick Links</p>
              <ul className="space-y-3 text-sm text-white/60">
                <li><button onClick={() => navigate("/")} className="hover:text-white transition-colors">What We Do</button></li>
                <li><button onClick={() => navigate("/infrastructure")} className="hover:text-white transition-colors">Infrastructure Services</button></li>
                <li><button onClick={() => navigate("/platforms")} className="hover:text-white transition-colors">Software Platforms</button></li>
                <li><button onClick={() => navigate("/confidential")} className="hover:text-white transition-colors">Request Access</button></li>
              </ul>
            </div>

            {/* Information */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Information</p>
              <p className="text-xs text-white/50 leading-relaxed">
                For authorised stakeholder access and infrastructure enquiries,
                please use the secure access portal.
              </p>
            </div>

            {/* Legal */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-4">Legal &amp; Compliance</p>
              <ul className="space-y-3 text-sm text-white/60">
                <li><button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms &amp; Conditions</button></li>
                <li><button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy Policy</button></li>
                <li><button onClick={() => navigate("/compliance")} className="hover:text-white transition-colors">POPIA Compliance</button></li>
              </ul>
            </div>

          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/30">
            <p>&copy; {new Date().getFullYear()} LEO Group Holding Company. All rights reserved.</p>
            <p>
              Designed &amp; developed by{" "}
              <a
                href="https://www.beyondic.co.za"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white/60 transition-colors underline underline-offset-2"
              >
                Beyond Imagination
              </a>
            </p>
          </div>

        </div>
      </footer>

    </div>
  );
}
